import { useEffect, useRef } from 'react'

const particleColors = ['#8b5cf6', '#22d3ee', '#6366f1', '#ec4899', '#f5c451']

function makeParticles(count, width, height) {
  return Array.from({ length: count }, (_, index) => ({
    x: Math.random() * width,
    y: height * (0.18 + Math.random() * 0.64),
    base: Math.random() * Math.PI * 2,
    size: 0.55 + Math.random() * 1.45,
    speed: 0.08 + Math.random() * 0.26,
    color: particleColors[index % particleColors.length],
  }))
}

function resolveFont(context, text, width, height) {
  let size = Math.min(height * 0.68, width / 4.35)

  while (size > 20) {
    context.font = `820 ${size}px Fraunces, "Noto Serif SC", "Noto Serif TC", serif`
    if (context.measureText(text).width <= width * 0.96) break
    size -= 3
  }

  return context.font
}

export function TextMaskWordmark({ text = 'Chicross Studio', reducedMotion = false }) {
  const canvasRef = useRef(null)

  useEffect(() => {
    const canvas = canvasRef.current
    const context = canvas.getContext('2d', { alpha: true })
    let frame = 0
    let particles = []
    let hidden = document.hidden

    const resize = () => {
      const rect = canvas.getBoundingClientRect()
      const dpr = Math.min(window.devicePixelRatio || 1, 1.5)
      canvas.width = Math.max(1, Math.floor(rect.width * dpr))
      canvas.height = Math.max(1, Math.floor(rect.height * dpr))
      context.setTransform(dpr, 0, 0, dpr, 0, 0)
      const targetCount = rect.width < 720 ? 640 : 1040
      particles = makeParticles(targetCount, rect.width, rect.height)
    }

    const drawTextMask = (width, height) => {
      context.save()
      context.globalCompositeOperation = 'destination-in'
      context.fillStyle = '#fff'
      context.textAlign = 'center'
      context.textBaseline = 'middle'
      resolveFont(context, text, width, height)
      context.fillText(text, width / 2, height / 2)
      context.restore()
      context.globalCompositeOperation = 'source-over'
    }

    const draw = (time = 0) => {
      const rect = canvas.getBoundingClientRect()
      const width = rect.width
      const height = rect.height
      const phase = reducedMotion ? 0.45 : ((time / 12000) % 1)
      const gradient = context.createLinearGradient(
        -width + phase * width * 2,
        0,
        width + phase * width * 2,
        0,
      )

      context.clearRect(0, 0, width, height)
      gradient.addColorStop(0, particleColors[0])
      gradient.addColorStop(0.2, particleColors[1])
      gradient.addColorStop(0.42, particleColors[4])
      gradient.addColorStop(0.62, particleColors[3])
      gradient.addColorStop(0.82, particleColors[2])
      gradient.addColorStop(1, particleColors[0])
      context.fillStyle = gradient
      context.fillRect(0, 0, width, height)
      context.globalCompositeOperation = 'screen'
      context.globalAlpha = 0.58

      for (const particle of particles) {
        if (!reducedMotion) {
          particle.x += particle.speed + Math.sin(time / 1100 + particle.base) * 0.14
          particle.y += Math.cos(time / 1400 + particle.base) * 0.12
          if (particle.x > width + 8) particle.x = -8
          if (particle.y < height * 0.12) particle.y = height * 0.7
          if (particle.y > height * 0.82) particle.y = height * 0.18
        }

        const glow = context.createRadialGradient(
          particle.x,
          particle.y,
          0,
          particle.x,
          particle.y,
          particle.size * 6,
        )
        glow.addColorStop(0, `${particle.color}9a`)
        glow.addColorStop(1, `${particle.color}00`)
        context.fillStyle = glow
        context.beginPath()
        context.arc(particle.x, particle.y, particle.size * 6, 0, Math.PI * 2)
        context.fill()
      }

      context.globalAlpha = 1
      drawTextMask(width, height)
    }

    const animate = (time) => {
      if (!hidden) draw(time)
      if (!reducedMotion) frame = requestAnimationFrame(animate)
    }

    const onVisibility = () => {
      hidden = document.hidden
    }

    resize()
    draw(0)
    if (!reducedMotion) frame = requestAnimationFrame(animate)
    window.addEventListener('resize', resize)
    document.addEventListener('visibilitychange', onVisibility)

    return () => {
      cancelAnimationFrame(frame)
      window.removeEventListener('resize', resize)
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [reducedMotion, text])

  return (
    <div className="text-mask-wordmark" aria-hidden="true">
      <canvas ref={canvasRef} className="text-mask-wordmark__canvas" />
    </div>
  )
}
